import { useTranslations } from "next-intl"

import { moneyShort } from "@/components/marketplace/format"
import { HeadingMark } from "@/components/marketplace/layout/heading-mark"
import { ArrowIcon, Heading, LinkButton, Row, Stack, Text } from "@/components/ui"
import { type ImageCrop, TileImage } from "@/components/ui/tile-image"

interface CtaChip {
  label: string
}

interface CtaBandProps {
  variant?: "default" | "app"
  eyebrow?: string
  title: string
  accent?: string
  sub?: string
  chips?: CtaChip[]
  cta?: string
  ctaHref?: string
  image?: string
  imageCrop?: ImageCrop
  imageTall?: string
  imageAlt?: string
}

type CopyProps = Omit<CtaBandProps, "variant" | "image" | "imageTall" | "imageAlt"> & { onBrand?: boolean; className?: string }

const SAMPLE_TOTAL = 1200
const SAMPLE_COUNT = 4
const SAMPLE_PAID = 1

const Receipt = ({ className }: { className: string }) => {
  const t = useTranslations("marketplace.promo")
  const each = SAMPLE_TOTAL / SAMPLE_COUNT
  return (
    <Stack gap="300" aria-hidden className={`rounded-16x bg-surface shadow-modal p-400 ${className}`}>
      <Row justify="between" className="font-text text-body-sm text-fg-muted tabular-nums">
        <span>{t("receiptOrder", { total: moneyShort(SAMPLE_TOTAL) })}</span>
        <span>{t("receiptProgress", { paid: SAMPLE_PAID, count: SAMPLE_COUNT })}</span>
      </Row>
      <Row gap="150">
        {Array.from({ length: SAMPLE_COUNT }, (_, index) => (
          <span key={index} className={`h-1.5 flex-1 rounded-full ${index < SAMPLE_PAID ? "bg-brand" : "bg-surface-muted"}`} />
        ))}
      </Row>
      <Row gap="300" align="center" className="bg-brand-subtle rounded-12x px-350 py-300">
        <Text as="span" variant="captionLg" family="display" tone="inverted" className="bg-brand grid size-6 shrink-0 place-items-center rounded-full font-black">
          N
        </Text>
        <Text as="span" variant="bodySm" family="display" tone="brand" className="flex-1 font-semibold">
          {t("receiptNext")}
        </Text>
        <Text as="b" variant="bodyLg" family="display" tone="brand" numeric>
          {moneyShort(each)}
        </Text>
      </Row>
      <Row gap="300" align="baseline" className="border-line-subtle border-t pt-300">
        <Text as="span" variant="bodySm" family="display" tone="strong" className="flex-1 font-semibold">
          {t("receiptDate")}
        </Text>
        <Text as="span" variant="captionLg" family="text" tone="muted" numeric>
          {t("receiptRemaining", { amount: moneyShort(each * (SAMPLE_COUNT - SAMPLE_PAID)) })}
        </Text>
      </Row>
    </Stack>
  )
}

const Copy = ({ eyebrow, title, accent, sub, chips, cta, ctaHref, onBrand = false, className }: CopyProps) => (
  <Stack gap="500" className={className}>
    {eyebrow && (
      <Row as="span" gap="200" align="center">
        <span className={`size-1.5 rounded-full ${onBrand ? "bg-surface-paper" : "bg-brand"}`} aria-hidden />
        <Text as="span" variant="eyebrow" tone="inherit" className="text-on-scrim-muted">
          {eyebrow}
        </Text>
      </Row>
    )}
    <Heading as="h2" variant="displayLg" family="display" tone="inverted" balance className="@3xl:text-display-2xl leading-none font-black tracking-tighter">
      {title}
      {accent && <HeadingMark>{accent}</HeadingMark>}
    </Heading>
    {sub && (
      <Text as="p" variant="bodyLg" family="text" tone="inherit" className={`max-w-measure text-pretty ${onBrand ? "text-fg-inverted" : "text-fg-inverted-muted"}`}>
        {sub}
      </Text>
    )}
    {chips && chips.length > 0 && (
      <Row wrap gap="400" className="font-display text-body-sm text-on-scrim-muted font-semibold">
        {chips.map((chip) => (
          <span key={chip.label}>{chip.label}</span>
        ))}
      </Row>
    )}
    {cta && (
      <LinkButton href={ctaHref} variant={onBrand ? "ink" : "primary"} size="lg" className="lift mt-200 self-start font-extrabold" iconRight={<ArrowIcon size={18} />}>
        {cta}
      </LinkButton>
    )}
  </Stack>
)

// The page's last photograph, desaturated against a brand-blue ground rather than
// laid on top of it — luminosity is what makes one image and one colour read as a
// single duotone object, and it needs the ground in the same stacking context, so
// the picture layer must not open one of its own.
const AppBand = ({ image, imageCrop, imageTall, imageAlt, ...copy }: CtaBandProps) => (
  <div className="@container">
    <div className="rounded-20x bg-brand @4xl:min-h-app-band @container relative isolate w-full overflow-hidden">
      <span className="absolute inset-0">
        {imageTall && <TileImage src={imageTall} alt={imageAlt ?? ""} fill sizes="100vw" className="object-cover mix-blend-luminosity @4xl:hidden" />}
        <TileImage
          src={image ?? ""}
          crop={imageCrop}
          alt={imageAlt ?? ""}
          fill
          sizes="100vw"
          className={`object-app-band object-cover mix-blend-luminosity ${imageTall ? "hidden @4xl:block" : ""}`}
        />
      </span>
      <span
        aria-hidden
        className="from-brand via-brand/95 to-brand/10 @4xl:from-brand/95 @4xl:via-brand/70 @4xl:to-brand/0 z-pill absolute inset-0 bg-linear-to-t via-46% to-76% @4xl:bg-linear-to-r @4xl:via-40% @4xl:to-66%"
      />
      <div aria-hidden className="h-app-stage @4xl:hidden" />
      <Copy {...copy} onBrand className="z-dropdown @4xl:max-w-app-copy @4xl:px-hero-inset relative px-600 pb-600 @4xl:absolute @4xl:top-1/2 @4xl:-translate-y-1/2 @4xl:py-0" />
      <Receipt className="z-sticky pointer-events-none relative mx-600 mb-800 max-w-100 @4xl:absolute @4xl:right-1200 @4xl:bottom-1600 @4xl:mx-0 @4xl:mb-0 @4xl:w-72" />
    </div>
  </div>
)

export const CtaBand = ({ variant = "default", image, imageTall, imageAlt, ...copy }: CtaBandProps) =>
  variant === "app" && image ? (
    <AppBand image={image} imageTall={imageTall} imageAlt={imageAlt} {...copy} />
  ) : (
    <div className="rounded-20x bg-nelo-black-soft text-fg-inverted motion-safe:animate-rise-in @container relative isolate w-full overflow-hidden px-600 py-800 @3xl:px-1200 @3xl:py-1200">
      <div className="grid items-center gap-800 @3xl:grid-cols-2">
        <Copy {...copy} className="max-w-measure-lg" />
        <Receipt className="w-full max-w-100 @3xl:justify-self-end" />
      </div>
    </div>
  )
